import { useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useRef } from 'react';

import type { BusinessRelationshipDetail } from '@/entities/business-relationship/api/businessRelationship.api';
import type { RelationshipGroup } from '@/entities/business-relationship/lib/statusView';
import {
  toTradeSections,
  type TradeRow,
} from '@/entities/business-relationship/lib/tradeRows';
import { useDrugDrawer } from '@/shared/components/drug-drawer/useDrugDrawer';
import { Badge } from '@/shared/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
} from '@/shared/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { cn } from '@/shared/lib/utils';
import { formatWon } from '@/shared/utils/format';
import { useUIStore } from '@/stores/uiStore';

import { formatSpacedDate } from '../utils';
import { AppOnlyActionButton } from './AppOnlyActionButton';
import { DrawerHeader, DrawerListCard, TabCount } from './drawerParts';

const TAB_ORDER: RelationshipGroup[] = ['ongoing', 'applying', 'closed'];
const TAB_LABEL: Record<RelationshipGroup, string> = {
  ongoing: '거래중',
  applying: '필터링 신청',
  closed: '종료·거절',
};

const emptyMessage = (tab: RelationshipGroup, isHospital: boolean) => {
  const subject = isHospital ? '의약품이' : '제약사가';
  switch (tab) {
    case 'ongoing':
      return `거래중인 ${subject} 없어요.`;
    case 'applying':
      return `필터링 신청 중인 ${subject} 없어요.`;
    default:
      return `종료되거나 거절된 ${subject} 없어요.`;
  }
};

/** 회색 4px 점으로 이은 한 줄 (Figma row · dot 840:4878) */
const DotRow = ({ parts }: { parts: string[] }) => (
  <p className="flex flex-wrap items-center gap-x-2 text-body-15-r text-fg-tertiary">
    {parts.map((p, i) => (
      <span key={`${i}-${p}`} className="flex items-center gap-2">
        {i > 0 && (
          <span className="size-1 rounded-full bg-grey-400" aria-hidden />
        )}
        {p}
      </span>
    ))}
  </p>
);

/**
 * Figma 거래처_거래정보 (840:4980) — 드로어 행 하나.
 * 의원_기본 840:4962(신청은 배지) · 병원_기본 840:5500(제약사 · 날짜 · 최소 금액) · 종료 840:6481(배지 · 날짜 + [필터링 신청])
 * 외부 계약(840:4963 · 840:5507 "{업체이름} 계약")은 API에 값이 없어 그리지 않는다.
 */
const TradeInfoRow = ({ row }: { row: TradeRow }) => {
  const date = formatSpacedDate(row.date);
  const parts = [
    row.pharmaCompanyName,
    date && `${date} ${row.dateSuffix}`,
    row.group !== 'closed' &&
      (row.transferPending
        ? '타 CSO 이관 확인 중'
        : row.minPrescriptionAmount !== null &&
          `최소 처방 금액 ${formatWon(row.minPrescriptionAmount)}`),
  ].filter(Boolean) as string[];

  return (
    <div className="flex items-start gap-1">
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col items-start',
          row.group === 'closed' ? 'gap-2' : 'gap-1',
        )}
      >
        {row.badge && <Badge {...row.badge.style}>{row.badge.label}</Badge>}
        <p className="text-subtitle-16-b break-keep text-fg-primary">
          {row.title}
        </p>
        {parts.length > 0 && <DotRow parts={parts} />}
      </div>
      {row.group === 'closed' && (
        <AppOnlyActionButton
          feature="filteringApply"
          variant="grey"
          size="small"
        >
          필터링 신청
        </AppOnlyActionButton>
      )}
    </div>
  );
};

/**
 * M-19 · M-20 거래 정보 드로어 (640, Figma 835:8886 · 840:6696 · 840:5628 · 840:6409) — 조회 전용.
 * 의원급은 제약사, 병원급은 의약품 단위. 상세 응답의 trades 세 섹션을 탭(거래중 · 필터링 신청 · 종료·거절)으로 나눠 보인다.
 * v1.0: 검색 · 정렬을 뺐다 (서버 기본 정렬 그대로).
 * 병원급 거래중 행을 누르면 M-12 드로어가 이 드로어 위에 24px 비켜 겹친다 (M-15 B 2단).
 */
export const TradeInfoDrawer = ({
  detail,
  isHospital,
  tab,
  onTabChange,
  onClose,
}: {
  detail: BusinessRelationshipDetail;
  isHospital: boolean;
  /** 보고 있는 탭. null이면 닫힘 */
  tab: RelationshipGroup | null;
  onTabChange: (tab: RelationshipGroup) => void;
  onClose: () => void;
}) => {
  const open = tab !== null;
  const unit = isHospital ? '의약품' : '제약사';
  const setStacked = useUIStore((s) => s.setDrugDrawerStacked);
  const { open: openDrug } = useDrugDrawer();
  const { drug } = useSearch({ strict: false }) as { drug?: string };
  /** 위에 겹친 M-12 드로어가 닫히면 포커스를 눌렀던 행으로 돌려준다 */
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const sections = useMemo(
    () => toTradeSections(detail.trades, isHospital),
    [detail.trades, isHospital],
  );
  const counts = detail.tradeStatusCounts;

  const openStackedDrug = (code: string) => {
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    void openDrug(code);
  };

  useEffect(() => {
    const target = returnFocusRef.current;
    if (drug || !open || !target) return;
    returnFocusRef.current = null;
    const id = window.setTimeout(() => target.focus(), 0);
    return () => window.clearTimeout(id);
  }, [drug, open]);

  // 이 드로어가 열려 있는 동안 M-12 드로어는 2단 스택으로 연다
  useEffect(() => {
    setStacked(open);
    return () => setStacked(false);
  }, [open, setStacked]);

  const rows = tab ? sections[tab] : [];
  const clickable = isHospital && tab === 'ongoing';

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="bg-surface-grey"
        // 위에 M-12 드로어가 겹치면 포커스가 그쪽으로 옮겨 간다. 그때 이 드로어가 닫히지 않게 한다
        onFocusOutside={(e) => e.preventDefault()}
      >
        <Tabs
          value={tab ?? 'ongoing'}
          onValueChange={(v) => onTabChange(v as RelationshipGroup)}
          className="flex min-h-0 flex-1 flex-col"
        >
          <DrawerHeader
            title={`${unit} 거래 정보`}
            clinicName={detail.clinicName}
            clinicTypeLabel={detail.clinicTypeLabel}
            onClose={onClose}
          >
            <TabsList className="w-full pt-2">
              {TAB_ORDER.map((t) => (
                <TabsTrigger key={t} value={t} className="flex-1">
                  <TabCount label={TAB_LABEL[t]} count={counts[t]} />
                </TabsTrigger>
              ))}
            </TabsList>
          </DrawerHeader>
          <SheetDescription className="sr-only">
            {detail.clinicName}의 거래 상태별 {unit} 목록
          </SheetDescription>
          <div className="min-h-0 flex-1 overflow-y-auto p-6">
            {rows.length === 0 ? (
              <p className="rounded-20 bg-surface-white px-6 py-10 text-center text-body-14-m text-fg-tertiary">
                {tab && emptyMessage(tab, isHospital)}
              </p>
            ) : (
              <DrawerListCard>
                {rows.map((row) => (
                  <li key={row.key}>
                    {clickable && row.drugCode ? (
                      <button
                        type="button"
                        onClick={() => openStackedDrug(row.drugCode!)}
                        className="-m-2 block w-[calc(100%+16px)] rounded-12 p-2 text-left outline-none hover:bg-surface-grey focus-visible:shadow-[0_0_0_3px_var(--color-brand-200)]"
                      >
                        <TradeInfoRow row={row} />
                      </button>
                    ) : (
                      <TradeInfoRow row={row} />
                    )}
                  </li>
                ))}
              </DrawerListCard>
            )}
          </div>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};
